// src/components/lesson/diagrams/UnitCircle.tsx
//
// Unit circle with optional angle ray, sin/cos perpendiculars, and
// quadrant tick marks. Useful for trig: sin/cos definition, special
// angles, reference-angle questions.
//
// Convention: standard math orientation — positive x to the right,
// positive y UP (so we negate y when projecting to SVG screen coords
// where y grows downward). Angle measured counter-clockwise from the
// positive x-axis, in degrees.
//
// Params:
//   {
//     "angleDeg": 30,
//     "showSinCos": true,         // draws perpendiculars + labels them
//     "pointLabel": "(√3/2, 1/2)",// shown next to the point on the circle
//     "marks": [0, 90, 180, 270]  // small ticks at named axis angles
//   }

type UnitCircleParams = {
  angleDeg?: number
  showSinCos?: boolean
  pointLabel?: string
  marks?: number[]
  label?: string
}

const W = 280
const H = 280
// Center the circle in the SVG with room for tick labels around it.
const CENTER = { x: W / 2, y: H / 2 }
const RADIUS = 90

export default function UnitCircle({ params }: { params: Record<string, unknown> }) {
  const p = params as UnitCircleParams
  const angleDeg = typeof p.angleDeg === 'number' ? p.angleDeg : 60
  const showSinCos = p.showSinCos === true
  const marks = Array.isArray(p.marks)
    ? p.marks.filter((n): n is number => typeof n === 'number')
    : []

  // Convert angle (degrees, math convention CCW from +x) to SVG coords.
  const rad = (angleDeg * Math.PI) / 180
  const point = {
    x: CENTER.x + RADIUS * Math.cos(rad),
    y: CENTER.y - RADIUS * Math.sin(rad), // negate y for SVG screen
  }

  // Foot of perpendicular drop from the point to the x-axis (for cos).
  const foot = { x: point.x, y: CENTER.y }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? `Unit circle, angle ${angleDeg}°`}
      className="my-3 w-full max-w-xs rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* x-axis */}
      <line
        x1={CENTER.x - RADIUS - 18}
        y1={CENTER.y}
        x2={CENTER.x + RADIUS + 18}
        y2={CENTER.y}
        stroke="rgb(100 116 139)"
        strokeWidth="1"
      />
      {/* y-axis */}
      <line
        x1={CENTER.x}
        y1={CENTER.y - RADIUS - 18}
        x2={CENTER.x}
        y2={CENTER.y + RADIUS + 18}
        stroke="rgb(100 116 139)"
        strokeWidth="1"
      />

      {/* Unit circle */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={RADIUS}
        fill="none"
        stroke="rgb(148 163 184)"
        strokeWidth="1.5"
      />

      {/* Tick marks at named axis angles (typically 0/90/180/270/360) */}
      {marks.map((m) => {
        const r = (m * Math.PI) / 180
        const tick1 = {
          x: CENTER.x + (RADIUS - 5) * Math.cos(r),
          y: CENTER.y - (RADIUS - 5) * Math.sin(r),
        }
        const tick2 = {
          x: CENTER.x + (RADIUS + 5) * Math.cos(r),
          y: CENTER.y - (RADIUS + 5) * Math.sin(r),
        }
        // Label position — push outside the tick a bit further so it
        // doesn't crowd the circle.
        const labelPos = {
          x: CENTER.x + (RADIUS + 18) * Math.cos(r),
          y: CENTER.y - (RADIUS + 18) * Math.sin(r),
        }
        return (
          <g key={`mark-${m}`}>
            <line
              x1={tick1.x}
              y1={tick1.y}
              x2={tick2.x}
              y2={tick2.y}
              stroke="rgb(148 163 184)"
              strokeWidth="1.25"
            />
            <text
              x={labelPos.x}
              y={labelPos.y}
              fontSize="10"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="rgb(148 163 184)"
            >
              {m}°
            </text>
          </g>
        )
      })}

      {/* Sin/cos perpendiculars (drawn UNDER the angle ray + point so they
          don't visually overpower the main angle indicator). */}
      {showSinCos && (
        <>
          {/* cos = horizontal segment from origin to foot */}
          <line
            x1={CENTER.x}
            y1={CENTER.y}
            x2={foot.x}
            y2={foot.y}
            stroke="rgb(251 191 36)"
            strokeWidth="2"
          />
          {/* sin = vertical segment from foot to point */}
          <line
            x1={foot.x}
            y1={foot.y}
            x2={point.x}
            y2={point.y}
            stroke="rgb(34 211 238)"
            strokeWidth="2"
          />
          {/* Right-angle marker at the foot of the perpendicular */}
          {Math.abs(foot.x - CENTER.x) > 14 && Math.abs(foot.y - point.y) > 14 && (
            <polyline
              points={
                // Marker square between cos (horizontal) and sin (vertical).
                // Direction depends on which quadrant we're in.
                point.y < CENTER.y
                  ? // upper half — marker goes UP from the foot
                    `${foot.x - 8 * Math.sign(foot.x - CENTER.x)},${foot.y} ${foot.x - 8 * Math.sign(foot.x - CENTER.x)},${foot.y - 8} ${foot.x},${foot.y - 8}`
                  : // lower half — marker goes DOWN from the foot
                    `${foot.x - 8 * Math.sign(foot.x - CENTER.x)},${foot.y} ${foot.x - 8 * Math.sign(foot.x - CENTER.x)},${foot.y + 8} ${foot.x},${foot.y + 8}`
              }
              fill="none"
              stroke="rgb(148 163 184)"
              strokeWidth="1"
            />
          )}
          {/* "cos θ" label centered on horizontal segment */}
          <text
            x={(CENTER.x + foot.x) / 2}
            y={CENTER.y + 14}
            fontSize="11"
            fontWeight="600"
            textAnchor="middle"
            fill="rgb(251 191 36)"
          >
            cos
          </text>
          {/* "sin θ" label on vertical segment, offset away from circle */}
          <text
            x={foot.x + 8 * Math.sign(foot.x - CENTER.x || 1)}
            y={(foot.y + point.y) / 2}
            fontSize="11"
            fontWeight="600"
            textAnchor={foot.x >= CENTER.x ? 'start' : 'end'}
            dominantBaseline="middle"
            fill="rgb(34 211 238)"
          >
            sin
          </text>
        </>
      )}

      {/* Angle ray from origin to point */}
      <line
        x1={CENTER.x}
        y1={CENTER.y}
        x2={point.x}
        y2={point.y}
        stroke="rgb(244 114 182)"
        strokeWidth="2"
      />

      {/* Small arc near origin showing the swept angle. Limit arc radius
          so it stays close to the origin and doesn't crowd the point. */}
      {(() => {
        const arcR = 22
        const startX = CENTER.x + arcR
        const startY = CENTER.y
        const endX = CENTER.x + arcR * Math.cos(rad)
        const endY = CENTER.y - arcR * Math.sin(rad)
        const largeArc = Math.abs(angleDeg % 360) > 180 ? 1 : 0
        // sweep=0 because in SVG y grows down; for CCW visual sweep we
        // want sweep flag 0.
        return (
          <path
            d={`M ${startX} ${startY} A ${arcR} ${arcR} 0 ${largeArc} 0 ${endX} ${endY}`}
            fill="none"
            stroke="rgb(244 114 182)"
            strokeWidth="1.5"
          />
        )
      })()}

      {/* Angle label inside the arc */}
      <text
        x={CENTER.x + 32 * Math.cos(rad / 2)}
        y={CENTER.y - 32 * Math.sin(rad / 2)}
        fontSize="11"
        fontWeight="600"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="rgb(244 114 182)"
      >
        {angleDeg}°
      </text>

      {/* The point on the circle */}
      <circle
        cx={point.x}
        cy={point.y}
        r="4"
        fill="rgb(244 114 182)"
        stroke="rgb(15 23 42)"
        strokeWidth="1.5"
      />
      {p.pointLabel && (
        <text
          // Place label just outside the circle along the radial direction
          x={CENTER.x + (RADIUS + 12) * Math.cos(rad)}
          y={CENTER.y - (RADIUS + 12) * Math.sin(rad)}
          fontSize="10"
          fontWeight="600"
          textAnchor={point.x >= CENTER.x ? 'start' : 'end'}
          dominantBaseline={point.y <= CENTER.y ? 'auto' : 'hanging'}
          fill="rgb(244 114 182)"
        >
          {p.pointLabel}
        </text>
      )}
    </svg>
  )
}
